import React, { useEffect, useRef, useState } from "react";

import { Icon, IconButton, Textarea } from "../components/ui";
import { formatBytes } from "./chatFormat";
import { extensionLabel, iconForKind, kindOf } from "./attachments";

/**
 * The chat composer: text (Enter sends, Shift+Enter breaks) and photo /
 * video / file attachments, which queue as removable previews until Send.
 *
 * Queued images show a real thumbnail rather than a generic chip, so a file
 * looks the same before and after sending — previously an image became a
 * grey chip in the composer and a photo in the thread, which made every send
 * feel like a different thing had happened.
 *
 * No voice recorder — removed on Alex's call (2026-09-28). Voice notes sent
 * from the Roam side still arrive and play in the thread; we simply do not
 * offer recording here.
 *
 * `onStartMeeting` is passed only for RC master admins, who hold the only
 * Roam seats. It sits beside the paperclip because a meeting link is a thing
 * you *send* into the conversation.
 */

const MAX_FILES = 10;
const MAX_FILE_BYTES = 100 * 1024 * 1024; // matches the API's 100 MB cap
const MAX_BODY_CHARS = 8000; // matches the API's `body` validation

/**
 * Object URLs for image previews, revoked when the queue changes.
 *
 * `createObjectURL` is absent in jsdom, and a thumbnail is a nicety — so a
 * missing implementation falls back to the icon treatment rather than
 * throwing inside an effect, which unmounts the whole thread.
 */
const canMakeObjectUrls =
  typeof URL !== "undefined" && typeof URL.createObjectURL === "function";

const useImagePreviews = (files) => {
  const [urls, setUrls] = useState([]);

  useEffect(() => {
    if (!canMakeObjectUrls) {
      setUrls([]);

      return undefined;
    }

    const made = files.map((file) =>
      kindOf({ name: file.name, mime: file.type }) === "image"
        ? URL.createObjectURL(file)
        : null
    );

    setUrls(made);

    return () => made.forEach((url) => url && URL.revokeObjectURL(url));
  }, [files]);

  return urls;
};

const Composer = ({ onSend, onStartMeeting, disabled = false, placeholder = "Write a message" }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const fileInput = useRef(null);
  const previews = useImagePreviews(files);

  const addFiles = (list) => {
    setError(null);
    const incoming = Array.from(list || []);
    const oversize = incoming.find((f) => f.size > MAX_FILE_BYTES);
    if (oversize) {
      setError(`"${oversize.name}" is over the 100 MB limit.`);
      return;
    }
    setFiles((current) => [...current, ...incoming].slice(0, MAX_FILES));
  };

  const removeFile = (index) =>
    setFiles((current) => current.filter((_, i) => i !== index));

  const canSend = !disabled && !sending && (text.trim() !== "" || files.length > 0);

  const doSend = () => {
    if (!canSend) return;
    const body = text.trim();
    const payload = files;
    setText("");
    setFiles([]);
    setSending(true);
    Promise.resolve(onSend(body || null, payload)).finally(() => setSending(false));
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      doSend();
    }
  };

  // Paste an image straight from the clipboard — the common case is a
  // screenshot, and saving it to disk first just to attach it is friction.
  const onPaste = (event) => {
    const pasted = Array.from(event.clipboardData?.files || []);

    if (pasted.length) {
      event.preventDefault();
      addFiles(pasted);
    }
  };

  return (
    <div className="rc-chat__composer">
      {files.length > 0 && (
        <div className="rc-chat__queue">
          {files.map((file, index) => {
            const kind = kindOf({ name: file.name, mime: file.type });
            const preview = previews[index];

            return (
              <div
                className={`rc-chat__queued rc-chat__queued--${kind}`}
                key={`${file.name}-${index}`}
              >
                {preview ? (
                  <img className="rc-chat__queued-thumb" src={preview} alt={file.name} />
                ) : (
                  <span className="rc-chat__queued-icon" aria-hidden="true">
                    <Icon name={iconForKind(kind)} size="sm" />
                    <span className="rc-chat__queued-ext">
                      {extensionLabel({ name: file.name, mime: file.type })}
                    </span>
                  </span>
                )}

                <span className="rc-chat__queued-main">
                  <span className="rc-chat__queued-name">{file.name}</span>
                  <span className="rc-chat__queued-size">{formatBytes(file.size)}</span>
                </span>

                <IconButton
                  className="rc-chat__queued-remove"
                  variant="ghost"
                  size="sm"
                  label={`Remove ${file.name}`}
                  onClick={() => removeFile(index)}
                >
                  <Icon name="X" size="xs" />
                </IconButton>
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="rc-chat__composer-error">{error}</div>}

      <div className="rc-chat__composer-row">
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />

        <IconButton
          variant="ghost"
          size="lg"
          label="Attach files"
          disabled={disabled}
          onClick={() => fileInput.current?.click()}
        >
          <Icon name="Paperclip" size="sm" />
        </IconButton>

        {/* `onStartMeeting` is only passed for RC master admins (see
            MessageThread), so a store admin never sees this button at all —
            the gate is upstream rather than here, and the backend refuses
            the endpoint with a 403 regardless.

            `Calendar` on Alex's call (2026-10-01). `Camera` read as "attach a
            photo" next to the paperclip, and `Users` read as "members"; a
            calendar is what people associate with a meeting. The icon set
            has no Video or Webcam glyph. */}
        {onStartMeeting && (
          <IconButton
            variant="ghost"
            size="lg"
            label="Start a meeting"
            disabled={disabled}
            onClick={onStartMeeting}
          >
            <Icon name="Calendar" size="sm" />
          </IconButton>
        )}

        {/* No `maxLength`: Textarea renders a "n / 8000" counter INSIDE its
            wrapper for that prop, which made the row taller than the field —
            and since the row aligns to flex-end, the buttons dropped below
            the input instead of sitting on its baseline. The prop was never
            forwarded to the <textarea> either, so it capped nothing; the
            limit is enforced on input here and by the API. */}
        <Textarea
          className="rc-chat__input"
          minRows={1}
          maxRows={6}
          placeholder={placeholder}
          value={text}
          disabled={disabled || sending}
          onChange={(event) => setText(event.target.value.slice(0, MAX_BODY_CHARS))}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />

        <IconButton
          size="lg"
          label="Send"
          disabled={!canSend}
          loading={sending}
          onClick={doSend}
        >
          <Icon name="Send" size="sm" />
        </IconButton>
      </div>
    </div>
  );
};

export default Composer;
